<template>
    <div v-if="open" class="action-modal__backdrop" @click.self="cancel">
        <section
            class="action-modal"
            :class="`action-modal--${tone}`"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
        >
            <span class="action-modal__accent" aria-hidden="true"></span>
            <header class="action-modal__header">
                <span class="action-modal__icon" aria-hidden="true"><slot name="icon"></slot></span>
                <div>
                    <small>{{ eyebrow }}</small>
                    <h2 :id="titleId">{{ title }}</h2>
                </div>
                <button
                    type="button"
                    class="action-modal__close"
                    title="Fechar"
                    :disabled="busy"
                    @click="cancel"
                ><XIcon /></button>
            </header>

            <p v-if="description" class="action-modal__description">{{ description }}</p>
            <div class="action-modal__content">
                <div v-if="error" class="action-modal__error" role="alert">
                    <AlertCircleIcon />
                    <span>{{ error }}</span>
                </div>
                <slot></slot>
            </div>

            <footer class="action-modal__actions">
                <button v-if="showCancel" type="button" class="action-modal__cancel" :disabled="busy" @click="cancel">
                    {{ cancelLabel }}
                </button>
                <button
                    type="button"
                    class="action-modal__confirm"
                    :disabled="busy || confirmDisabled"
                    @click="$emit('confirm')"
                >
                    <LoaderIcon v-if="busy" class="spin" />
                    {{ busy ? "Processando" : confirmLabel }}
                </button>
            </footer>
        </section>
    </div>
</template>

<script>
import { AlertCircleIcon, LoaderIcon, XIcon } from "vue-feather-icons";

export default {
    components: { AlertCircleIcon, LoaderIcon, XIcon },
    props: {
        open: { type: Boolean, default: false },
        title: { type: String, required: true },
        eyebrow: { type: String, default: "" },
        description: { type: String, default: "" },
        tone: { type: String, default: "warning" },
        confirmLabel: { type: String, required: true },
        cancelLabel: { type: String, default: "Voltar" },
        showCancel: { type: Boolean, default: true },
        busy: { type: Boolean, default: false },
        confirmDisabled: { type: Boolean, default: false },
        error: { type: String, default: "" }
    },
    computed: {
        titleId() {
            return `action-modal-title-${this._uid}`;
        }
    },
    watch: {
        open(value) {
            document.body.style.overflow = value ? "hidden" : "";
        }
    },
    mounted() {
        document.addEventListener("keydown", this.onKeydown);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.onKeydown);
        document.body.style.overflow = "";
    },
    methods: {
        cancel() {
            if (!this.busy) this.$emit("cancel");
        },
        onKeydown(event) {
            if (this.open && event.key === "Escape") this.cancel();
        }
    }
};
</script>

<style lang="scss" scoped>
.action-modal__backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: grid;
    place-items: center;
    padding: 18px;
    background: rgba(18, 24, 33, 0.58);
    backdrop-filter: blur(2px);
}

.action-modal {
    position: relative;
    width: min(560px, 100%);
    max-height: calc(100vh - 36px);
    overflow: auto;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 18px 50px rgba(28, 23, 20, 0.22);
    color: #4c4541;

    &__accent {
        position: absolute;
        inset: 0 0 auto;
        height: 5px;
        background: #c05a00;
    }

    &__header {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 13px;
        padding: 25px 26px 10px;

        small {
            display: block;
            color: #974900;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
        }

        h2 {
            margin: 3px 0 0;
            font-family: "Raleway";
            font-size: 20px;
            font-weight: 600;
        }
    }

    &__icon {
        width: 46px;
        height: 46px;
        display: grid;
        place-items: center;
        border: 1px solid #f0c7a6;
        border-radius: 50%;
        background: #fff1e8;
        color: #974900;

        ::v-deep svg { width: 22px; height: 22px; }
    }

    &__close {
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border: 0;
        border-radius: 5px;
        background: transparent;
        color: #7e7570;

        &:hover { background: #f3f1f0; }
    }

    &__description {
        margin: 0;
        padding: 0 26px;
        color: #7e7570;
        font-size: 13px;
        line-height: 1.5;
    }

    &__content { padding: 18px 26px 4px; }

    &__error {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 14px;
        border-left: 3px solid #b42318;
        border-radius: 4px;
        padding: 10px 12px;
        background: #fff0ef;
        color: #9f241b;
        font-size: 12px;
        line-height: 1.4;

        svg { width: 17px; height: 17px; flex: 0 0 auto; }
    }

    &__actions {
        display: flex;
        justify-content: flex-end;
        gap: 9px;
        margin-top: 18px;
        padding: 16px 26px 22px;
        border-top: 1px solid #ece0db;

        button {
            min-height: 46px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            border-radius: 5px;
            padding: 0 18px;
            font-weight: 600;
        }
    }

    &__cancel {
        border: 1px solid #cfc4be;
        background: #fff;
        color: #655d59;
    }

    &__confirm {
        border: 1px solid #a94e00;
        background: #a94e00;
        color: #fff;
    }

    &--success {
        .action-modal__accent { background: #237a3b; }
        .action-modal__header small { color: #237a3b; }
        .action-modal__icon { border-color: #b7ddc1; background: #e8f7ed; color: #237a3b; }
        .action-modal__confirm { border-color: #237a3b; background: #237a3b; }
    }
}

button:disabled { opacity: 0.45; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 560px) {
    .action-modal__backdrop { align-items: end; padding: 0; }
    .action-modal { width: 100%; max-height: 94vh; border-radius: 8px 8px 0 0; }
    .action-modal__header { padding: 22px 18px 10px; }
    .action-modal__description { padding: 0 18px; }
    .action-modal__content { padding: 16px 18px 2px; }
    .action-modal__actions {
        flex-direction: column-reverse;
        padding: 14px 18px 18px;

        button { width: 100%; min-height: 50px; }
    }
}
</style>
